export const WEBHOOK_PAGE_SIZE = 20;

const DATE_TIME_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

const TIME_FORMATTER = new Intl.DateTimeFormat('en-US', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const FULL_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  weekday: 'long',
  month: 'long',
  day: 'numeric',
});

const normalizeText = (value) => String(value ?? '').trim();

const toDayKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const parseJsonSafely = (value) => {
  if (!value) {
    return null;
  }

  if (typeof value === 'object') {
    return value;
  }

  if (typeof value !== 'string') {
    return null;
  }

  try {
    return JSON.parse(value);
  } catch (_error) {
    return null;
  }
};

const getObjectValue = (value, keys = []) => {
  if (!value || typeof value !== 'object') {
    return '';
  }

  for (const key of keys) {
    const candidate = value?.[key];

    if (typeof candidate === 'string' || typeof candidate === 'number') {
      const normalized = normalizeText(candidate);

      if (normalized) {
        return normalized;
      }
    }
  }

  return '';
};

const getAuthorName = (value) => {
  if (!value) {
    return '';
  }

  if (typeof value === 'string') {
    return normalizeText(value);
  }

  if (typeof value === 'object') {
    return (
      getObjectValue(value, ['name', 'login', 'username', 'full_name']) || ''
    );
  }

  return '';
};

const getRepositoryName = (value) => {
  if (!value) {
    return '';
  }

  if (typeof value === 'string') {
    return normalizeText(value);
  }

  if (typeof value === 'object') {
    return getObjectValue(value, ['full_name', 'name', 'repo', 'repository']);
  }

  return '';
};

const getCommitValue = (value) => {
  const normalized = normalizeText(value);

  if (!normalized) {
    return '';
  }

  return normalized.length > 10 ? normalized.slice(0, 7) : normalized;
};

const formatDuration = (value) => {
  if (value === null || value === undefined || value === '') {
    return '--';
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return value >= 1000 ? `${(value / 1000).toFixed(2)}s` : `${value}ms`;
  }

  const normalized = normalizeText(value);

  if (!normalized) {
    return '--';
  }

  if (/^\d+$/.test(normalized)) {
    const numericValue = Number(normalized);
    return numericValue >= 1000
      ? `${(numericValue / 1000).toFixed(2)}s`
      : `${numericValue}ms`;
  }

  return normalized;
};

const getStatusMeta = (value) => {
  const normalized = normalizeText(value).toLowerCase();

  if (
    normalized.includes('success') ||
    normalized.includes('ok') ||
    normalized.includes('delivered') ||
    normalized.includes('200')
  ) {
    return {
      key: 'success',
      label: 'success',
    };
  }

  if (
    normalized.includes('fail') ||
    normalized.includes('error') ||
    normalized.includes('timeout') ||
    normalized.includes('500') ||
    normalized.includes('400')
  ) {
    return {
      key: 'failed',
      label: 'failed',
    };
  }

  return {
    key: 'unknown',
    label: normalized || 'unknown',
  };
};

const parseBranchName = (value) => {
  const normalized = normalizeText(value);

  if (!normalized) {
    return '';
  }

  if (normalized.startsWith('refs/heads/')) {
    return normalized.replace('refs/heads/', '');
  }

  return normalized;
};

const normalizePayloadText = (value) => {
  if (value === null || value === undefined || value === '') {
    return 'Payload unavailable.';
  }

  if (typeof value === 'string') {
    const parsed = parseJsonSafely(value);

    if (parsed && typeof parsed === 'object') {
      return JSON.stringify(parsed, null, 2);
    }

    return value;
  }

  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2);
  }

  return String(value);
};

const getCreatedDate = (value) => {
  if (!value) {
    return null;
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date;
};

const getDerivedFields = (item, payloadObject = null) => {
  const sourcePayload = payloadObject || parseJsonSafely(item?.payload);
  const repositoryPayload =
    sourcePayload?.repository || sourcePayload?.repo || sourcePayload?.project;
  const senderPayload =
    sourcePayload?.sender || sourcePayload?.author || sourcePayload?.pusher;
  const headCommit = sourcePayload?.head_commit || sourcePayload?.commit || {};
  const pullRequestHead = sourcePayload?.pull_request?.head || {};

  const eventLabel =
    normalizeText(
      item?.event ||
        item?.event_name ||
        item?.event_type ||
        item?.webhook_event ||
        item?.topic ||
        sourcePayload?.event ||
        sourcePayload?.hook?.type ||
        sourcePayload?.action
    ) || 'delivery';

  const branchLabel =
    parseBranchName(
      item?.branch ||
        item?.branch_name ||
        item?.ref ||
        sourcePayload?.branch ||
        sourcePayload?.ref ||
        pullRequestHead?.ref
    ) || 'unknown';

  const commitLabel =
    getCommitValue(
      item?.commit ||
        item?.commit_sha ||
        item?.head_sha ||
        item?.sha ||
        sourcePayload?.after ||
        sourcePayload?.before ||
        headCommit?.id ||
        headCommit?.sha ||
        pullRequestHead?.sha
    ) || '--';

  const authorLabel =
    getAuthorName(item?.author) ||
    getAuthorName(item?.user) ||
    getAuthorName(senderPayload) ||
    getAuthorName(sourcePayload?.pull_request?.user) ||
    'Unknown author';

  const repoLabel =
    getRepositoryName(item?.repo) ||
    getRepositoryName(item?.repository) ||
    getRepositoryName(repositoryPayload) ||
    'Unknown repository';

  return {
    authorLabel,
    branchLabel,
    commitLabel,
    eventLabel,
    repoLabel,
  };
};

export const normalizeWebhookLog = (item, index = 0) => {
  const createdDate = getCreatedDate(
    item?.created_date || item?.createdAt || item?.delivered_at || item?.timestamp
  );
  const payloadObject = parseJsonSafely(item?.payload);
  const derived = getDerivedFields(item, payloadObject);
  const statusMeta = getStatusMeta(item?.status);

  return {
    key:
      item?.webhook_log_id ??
      item?.delivery_id ??
      item?.id ??
      `webhook-log-${index + 1}`,
    deliveryId:
      normalizeText(item?.delivery_id) ||
      normalizeText(item?.webhook_log_id) ||
      normalizeText(item?.id) ||
      `log-${index + 1}`,
    createdAt:
      item?.created_date || item?.createdAt || item?.delivered_at || item?.timestamp || '',
    createdAtLabel: createdDate ? DATE_TIME_FORMATTER.format(createdDate) : 'Unknown time',
    timeLabel: createdDate ? TIME_FORMATTER.format(createdDate) : '--:--',
    dayKey: createdDate ? toDayKey(createdDate) : 'unknown-day',
    statusKey: statusMeta.key,
    statusLabel: statusMeta.label,
    durationLabel: formatDuration(
      item?.duration || item?.duration_ms || item?.latency || item?.response_time
    ),
    payloadText: normalizePayloadText(item?.payload),
    ...derived,
    raw: item,
  };
};

export const normalizeWebhookDetail = (detailPayload, baseLog) => {
  const payloadObject = parseJsonSafely(detailPayload);
  const derived = getDerivedFields(baseLog?.raw || {}, payloadObject);

  return {
    payloadText: normalizePayloadText(detailPayload),
    ...derived,
  };
};

export const getAvatarInitials = (value) => {
  const normalized = normalizeText(value);

  if (!normalized) {
    return 'WH';
  }

  return normalized
    .split(/\s+/)
    .map((word) => word[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
};

const AVATAR_PALETTES = [
  'bg-amber-100 text-amber-900',
  'bg-emerald-100 text-emerald-900',
  'bg-sky-100 text-sky-900',
  'bg-rose-100 text-rose-900',
  'bg-violet-100 text-violet-900',
];

export const getAvatarPaletteClassName = (value) => {
  const normalized = normalizeText(value);

  if (!normalized) {
    return AVATAR_PALETTES[0];
  }

  const index =
    normalized
      .split('')
      .reduce((sum, character) => sum + character.charCodeAt(0), 0) %
    AVATAR_PALETTES.length;

  return AVATAR_PALETTES[index];
};

export const formatWebhookDayLabel = (dayKey) => {
  if (!dayKey || dayKey === 'unknown-day') {
    return 'Unknown day';
  }

  const today = new Date();
  const todayKey = toDayKey(today);

  if (dayKey === todayKey) {
    return 'Today';
  }

  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  if (dayKey === toDayKey(yesterday)) {
    return 'Yesterday';
  }

  const [year, month, day] = dayKey.split('-').map(Number);
  const parsed = new Date(year, (month || 1) - 1, day || 1);

  if (Number.isNaN(parsed.getTime())) {
    return dayKey;
  }

  return FULL_DATE_FORMATTER.format(parsed);
};

export const groupWebhookLogsByDay = (logs) => {
  const grouped = new Map();

  logs.forEach((log) => {
    const key = log.dayKey || 'unknown-day';
    const current = grouped.get(key) ?? [];
    current.push(log);
    grouped.set(key, current);
  });

  return Array.from(grouped.entries())
    .sort((first, second) => String(second[0]).localeCompare(String(first[0])))
    .map(([dayKey, items]) => {
      const successCount = items.filter((item) => item.statusKey === 'success').length;
      const failedCount = items.filter((item) => item.statusKey === 'failed').length;

      return {
        dayKey,
        label: formatWebhookDayLabel(dayKey),
        items,
        successCount,
        failedCount,
        totalCount: items.length,
      };
    });
};
